import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';

const AboutPage = () => {
  const [videoLoaded, setVideoLoaded] = useState(false);
  
  return (
    <div className="pb-20">
      {/* Hero Section with Video - Full Screen like Homepage */}
      <section className="relative h-screen overflow-hidden bg-black">
        {/* Loading Spinner - shown until video loads */}
        {!videoLoaded && (
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
              <p className="text-white/70 text-sm">Video yükleniyor...</p>
            </div>
          </div>
        )}
        
        {/* Poster/Placeholder Image - shown until video loads */}
        <img
          src="https://customer-assets.emergentagent.com/job_silk-fular/artifacts/t7z75kaw_VALMERA%20lOGO.png"
          alt="VALMÉRA"
          className={`absolute inset-0 w-full h-full object-contain bg-black p-20 transition-opacity duration-500 ${videoLoaded ? 'opacity-0' : 'opacity-100'}`}
        />
        
        <video
          autoPlay
          loop
          muted
          playsInline
          onCanPlay={() => setVideoLoaded(true)}
          onLoadedData={() => setVideoLoaded(true)}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${videoLoaded ? 'opacity-100' : 'opacity-0'}`}
        >
          <source src="https://customer-assets.emergentagent.com/job_d0961ac7-8764-4368-8149-51e14314d43e/artifacts/a6dpupll_a.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-black/20" />
        {/* Scroll Indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center text-white animate-bounce">
          <span className="text-xs tracking-wider mb-2">KAYDIR</span>
          <div className="w-px h-8 bg-white/50" />
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6">
        {/* Brand Story - After Video */}
        <section className="py-20 text-center">
          <img
            src="https://customer-assets.emergentagent.com/job_silk-fular/artifacts/t7z75kaw_VALMERA%20lOGO.png"
            alt="VALMÉRA"
            className="h-16 mx-auto mb-12"
          />
          <p className="text-xl md:text-2xl font-light leading-relaxed text-gray-700 max-w-3xl mx-auto mb-6">
            Valméra bizim için bir markadan çok daha fazlası. Çünkü on yıllık üç arkadaşın 
            dostluktan anlam ürettiği bir yolculuk.
          </p>
          <p className="text-lg text-gray-600 mb-8">
            Biz gücümüzü dostluğumuzdan alıyoruz.
          </p>
        </section>

        {/* Brand Philosophy */}
        <section className="py-16 border-t">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-gray-600 leading-relaxed text-lg mb-6">
              Fularlarımızda duyguyu merkeze alıyoruz. Her koleksiyonda bir olay örgüsünü 
              tasarıma dönüştürüyoruz.
            </p>
            <p className="text-2xl font-light italic text-gray-700 mb-6">
              "Biz, görüneni değil; hissedileni tasarlıyoruz."
            </p>
            <p className="text-gray-600 leading-relaxed">
              Kaliteyi bir ayrıcalık değil, bir sorumluluk olarak görüyoruz.
            </p>
          </div>
        </section>

        {/* Kökten Işığa Collection Story */}
        <section className="py-20 border-t">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <p className="text-sm tracking-[0.3em] text-gray-500 mb-4">KOLEKSİYON HİKAYESİ</p>
              <h2 className="text-4xl font-light tracking-wider">Kökten Işığa</h2>
            </div>
            
            <div className="space-y-8 text-gray-600 leading-relaxed">
              <p className="text-lg">
                Tasarımlarımızda hep duygularla başladık… Gördüğümüz, hissettiğimiz şeyleri 
                anlatmanın bir yolunu aradık. "Kökten Işığa" da öyle doğdu aslında.
              </p>
              
              <p>
                Ülkemizde sökülen zeytinlikleri gördük, içimiz acıdı. O köklerin yerinden 
                sökülmesi bize çok tanıdık geldi. Çünkü biz de bazen köklerimizden koparılıyoruz 
                — özellikle kadınlar. Kadına verilen değerin günden güne azaldığını görmek 
                bizi derinden etkiledi. O yüzden kadını ve zeytini aynı kökten düşündük.
              </p>
              
              <div className="text-center py-8 my-8 border-y border-gray-200">
                <p className="text-2xl font-light italic text-gray-700">
                  "Biri toprağın bereketi, diğeri yaşamın özü."
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-8 my-12">
                <div className="text-center">
                  <img
                    src="https://customer-assets.emergentagent.com/job_silk-fular/artifacts/gfklpc2m_1.png"
                    alt="Zeytin"
                    className="w-full aspect-square object-cover mb-4"
                  />
                  <p className="text-sm text-gray-500">İlk tasarımlarımızda sadece zeytin vardı…</p>
                  <p className="text-sm text-gray-500 italic">Çünkü hikâye orada başlıyordu.</p>
                </div>
                <div className="text-center">
                  <img
                    src="https://customer-assets.emergentagent.com/job_silk-fular/artifacts/k3ryxhlq_4.1.png"
                    alt="Zeytin ve Kadın"
                    className="w-full aspect-square object-cover mb-4"
                  />
                  <p className="text-sm text-gray-500">Sonra kadın belirmeye başladı —</p>
                  <p className="text-sm text-gray-500 italic">zeytinle yan yana, eşit bir yerde.</p>
                </div>
                <div className="text-center">
                  <img
                    src="https://customer-assets.emergentagent.com/job_silk-fular/artifacts/v4v9kxl5_6.png"
                    alt="Kadın"
                    className="w-full aspect-square object-cover mb-4"
                  />
                  <p className="text-sm text-gray-500">Zamanla kadın öne çıktı,</p>
                  <p className="text-sm text-gray-500 italic">tıpkı ışığın kökten doğması gibi.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Silk Section */}
        <section className="py-20 bg-black text-white -mx-6 px-6">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-2xl font-light tracking-wider text-center mb-12">
              VE BU HİKÂYEYİ İPEKLE ANLATTIK
            </h3>
            <div className="space-y-6 text-center">
              <p className="text-gray-300 leading-relaxed">
                Çünkü ipek, kadına yalnızca dokunuşuyla değil, ruhsal olarak da iyi gelir.
              </p>
              <p className="text-gray-300 leading-relaxed">
                Cildi besler, rahatlatır, nefes aldırır.
              </p>
              <p className="text-gray-300 leading-relaxed">
                Tıpkı bir şefkat dokunuşu gibi, yumuşak ama güçlüdür.
              </p>
              <p className="text-lg text-white italic mt-8">
                "Kadının içsel dengesini, zarafetini ve ışığını hatırlatır."
              </p>
            </div>
          </div>
        </section>

        {/* Awareness Section */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-xl text-gray-600 leading-relaxed mb-8">
              Biz bu geçişi sadece bir desen olarak değil, bir farkındalık olarak görüyoruz.
            </p>
            <div className="border-t pt-12 mt-12">
              <img
                src="https://customer-assets.emergentagent.com/job_silk-fular/artifacts/t7z75kaw_VALMERA%20lOGO.png"
                alt="VALMÉRA"
                className="h-12 mx-auto mb-6"
              />
              <p className="text-2xl font-light tracking-wider text-gray-700 italic">
                "Güzelliği yüzeyde değil, özde arayanlar için."
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

const ContactPage = () => {
  const [formData, setFormData] = React.useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitStatus, setSubmitStatus] = React.useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-6">
        {/* Page Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl font-light tracking-wider mb-4">İLETİŞİM</h1>
          <p className="text-gray-500 max-w-2xl mx-auto">
            Sorularınız, önerileriniz veya siparişleriniz için bizimle iletişime geçin.
            Size en kısa sürede dönüş yapacağız.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Form */}
          <div>
            <h2 className="text-2xl font-light tracking-wider mb-8">BİZE YAZIN</h2>
            
            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 text-green-800 rounded-lg">
                Mesajınız başarıyla gönderildi. En kısa sürede size dönüş yapacağız.
              </div>
            )}
            
            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-800 rounded-lg">
                Mesaj gönderilirken bir hata oluştu. Lütfen tekrar deneyin.
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Adınız *</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors"
                    placeholder="Adınızı girin"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Soyadınız *</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors"
                    placeholder="Soyadınızı girin"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">E-posta *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors"
                  placeholder="E-posta adresinizi girin"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Telefon</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors"
                  placeholder="Telefon numaranızı girin"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Konu *</label>
                <select
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors bg-white"
                >
                  <option value="">Konu seçin</option>
                  <option value="order">Sipariş Sorgulama</option>
                  <option value="product">Ürün Bilgisi</option>
                  <option value="return">İade & Değişim</option>
                  <option value="wholesale">Toptan Satış</option>
                  <option value="other">Diğer</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Mesajınız *</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors resize-none"
                  placeholder="Mesajınızı yazın..."
                />
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-black text-white py-4 text-sm tracking-wider hover:bg-gray-900 transition-colors flex items-center justify-center gap-2 disabled:bg-gray-400"
              >
                <Send size={16} />
                {isSubmitting ? 'GÖNDERİLİYOR...' : 'GÖNDER'}
              </button>
            </form>
          </div>

          {/* Contact Info */}
          <div>
            <h2 className="text-2xl font-light tracking-wider mb-8">İLETİŞİM BİLGİLERİ</h2>
            
            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <MapPin size={20} />
                </div>
                <div>
                  <h3 className="font-medium mb-2">Adres</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Uşak Merkez
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <Phone size={20} />
                </div>
                <div>
                  <h3 className="font-medium mb-2">Telefon & WhatsApp</h3>
                  <a 
                    href="https://wa.me/905465937071" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gray-600 text-sm hover:text-black transition-colors"
                  >
                    0546 593 70 71
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <Mail size={20} />
                </div>
                <div>
                  <h3 className="font-medium mb-2">E-posta</h3>
                  <a 
                    href="mailto:officialvalmera@gmail.com"
                    className="text-gray-600 text-sm hover:text-black transition-colors"
                  >
                    officialvalmera@gmail.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <Clock size={20} />
                </div>
                <div>
                  <h3 className="font-medium mb-2">Çalışma Saatleri</h3>
                  <p className="text-gray-600 text-sm">
                    Pazartesi - Cumartesi: 10:00 - 20:00
                    <br />
                    Pazar: 12:00 - 18:00
                  </p>
                </div>
              </div>
            </div>

            {/* Social Media */}
            <div className="mt-12 p-6 bg-gray-50 rounded-lg">
              <h3 className="font-medium mb-4">Sosyal Medyada Bizi Takip Edin</h3>
              <div className="flex gap-4">
                <a 
                  href="https://www.instagram.com/valmeraofficial/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-white border border-gray-200 flex items-center justify-center hover:bg-black hover:text-white transition-colors rounded-full"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
                <a 
                  href="https://www.youtube.com/@ValmeraOfficial" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-white border border-gray-200 flex items-center justify-center hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors rounded-full"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export { AboutPage, ContactPage };
